import { useEffect } from 'react';
import type { DocumentStatusSummary, PaginatedData } from '@libs/types';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { TenantProfileDocumentStatus } from '@libs/constants';

import { apiClient } from '../../../api/client';
import { queryKeys } from '../../../constants/query-keys';

import type {
  BulkExtractResponse,
  ConfirmUploadResponse,
  ExtractionReviewDto,
  GetDocumentStatusesResponse,
  ProfileReferenceFileDto,
  ProfileSummaryDto,
  RetryClassifyResponse,
  TenantProfileDocumentDto,
  UploadUrlResponse,
  VersionRef,
} from './types';
import { useBackendErrorToast } from './useBackendErrorToast';

const POLL_INTERVAL_MS = 5_000;

/** Document statuses that mean "something is still running" → keep polling. */
const BUSY_STATUSES: TenantProfileDocumentStatus[] = [
  TenantProfileDocumentStatus.CLASSIFYING,
  TenantProfileDocumentStatus.EXTRACTING,
];

async function fetchDocuments(
  categoryKey?: string,
): Promise<TenantProfileDocumentDto[]> {
  const { data } = await apiClient.get<TenantProfileDocumentDto[]>(
    'api/tenant-profile/documents',
    { params: categoryKey ? { categoryKey } : undefined },
  );
  return data;
}

/** Lightweight batch status lookup — the endpoint built for polling. */
async function fetchDocumentStatuses(
  ids: string[],
): Promise<GetDocumentStatusesResponse> {
  const { data } = await apiClient.get<GetDocumentStatusesResponse>(
    'api/tenant-profile/documents/status',
    { params: { documentIds: ids.join(',') } },
  );
  return data;
}

const busyIds = (docs: TenantProfileDocumentDto[]): string[] =>
  docs.filter((d) => BUSY_STATUSES.includes(d.status)).map((d) => d.id);

/**
 * Polls the dedicated `/documents/status` endpoint for the given in-flight ids
 * and patches the unified lifecycle `status` back into every cached document
 * list. When a document leaves the busy set we invalidate the full list once to
 * pull its authoritative DTO (extraction metadata, category, …). Mirrors the
 * assessment flow, which polls its lightweight `/status` route rather than
 * re-fetching the whole resource on every tick.
 */
function usePollDocumentStatuses(ids: string[]): void {
  const queryClient = useQueryClient();
  const enabled = ids.length > 0;
  const sortedIds = [...ids].sort();

  const { data } = useQuery({
    queryKey: queryKeys.tenantProfile.documents.statuses(sortedIds),
    queryFn: () => fetchDocumentStatuses(sortedIds),
    enabled,
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    const entries = data?.documents;
    if (!entries?.length) return;

    const current =
      queryClient.getQueryData<TenantProfileDocumentDto[]>(
        queryKeys.tenantProfile.documents.all,
      ) ?? [];

    // Did any polled document just leave the busy set?
    const settled = entries.some((e) => {
      const doc = current.find((d) => d.id === e.id);
      return (
        !!doc && doc.status !== e.status && !BUSY_STATUSES.includes(e.status)
      );
    });

    // Patch the lifecycle status into every cached document list (all + per-category).
    queryClient.setQueriesData<TenantProfileDocumentDto[]>(
      {
        predicate: (q) =>
          q.queryKey[0] === 'tenantProfile' &&
          q.queryKey[1] === 'documents' &&
          (q.queryKey[2] === 'all' || q.queryKey[2] === 'byCategory'),
      },
      (old) =>
        Array.isArray(old)
          ? old.map((doc) => {
              const entry = entries.find((e) => e.id === doc.id);
              return entry && entry.status !== doc.status
                ? { ...doc, status: entry.status }
                : doc;
            })
          : old,
    );

    if (settled) {
      queryClient.invalidateQueries({
        // Prefix-invalidate so the full list, the paginated hub table and the
        // status-summary counts all refresh.
        queryKey: ['tenantProfile', 'documents'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.summary,
      });
    }
  }, [data, queryClient]);
}

export const useListProfileDocuments = () => {
  const query = useQuery({
    queryKey: queryKeys.tenantProfile.documents.all,
    queryFn: () => fetchDocuments(),
  });
  usePollDocumentStatuses(busyIds(query.data ?? []));
  return query;
};

/** Documents filtered to a single classified category (per-category view). */
export const useListProfileDocumentsByCategory = (categoryKey: string) =>
  useQuery({
    queryKey: queryKeys.tenantProfile.documents.byCategory(categoryKey),
    queryFn: () => fetchDocuments(categoryKey),
    enabled: !!categoryKey,
  });

// ── Document hub table (backend pagination) ───────────────────────────────

export interface ListProfileDocumentsParams {
  page: number;
  limit: number;
  searchBy?: string;
  categoryKey?: string | null;
  /** Single derived lifecycle status (the hub filters one tab at a time). */
  status?: TenantProfileDocumentStatus | null;
}

async function fetchDocumentsPaginated(
  params: ListProfileDocumentsParams,
): Promise<PaginatedData<TenantProfileDocumentDto[]>> {
  const { data } = await apiClient.get<PaginatedData<TenantProfileDocumentDto[]>>(
    'api/tenant-profile/documents',
    {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.searchBy ? { searchBy: params.searchBy } : {}),
        ...(params.categoryKey ? { categoryKey: params.categoryKey } : {}),
        ...(params.status ? { status: params.status } : {}),
      },
    },
  );
  return data;
}

/**
 * Server-paginated document list for the hub table. Keeps the previous page
 * visible while the next loads, and polls while the current page still has a
 * document being classified/extracted.
 */
export const useListProfileDocumentsPaginated = (
  params: ListProfileDocumentsParams,
) =>
  useQuery({
    queryKey: queryKeys.tenantProfile.documents.paginated(params),
    queryFn: () => fetchDocumentsPaginated(params),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const rows = query.state.data?.rows ?? [];
      return rows.some((d) => BUSY_STATUSES.includes(d.status))
        ? POLL_INTERVAL_MS
        : false;
    },
  });

async function fetchStatusSummary(): Promise<DocumentStatusSummary> {
  const { data } = await apiClient.get<DocumentStatusSummary>(
    'api/tenant-profile/documents/status-summary',
  );
  return data;
}

/**
 * Per-status document totals for the hub's status strip / filter counts.
 * Polls while anything is still classifying/extracting so the counts keep up
 * with in-flight work without the hub loading the full document list.
 */
export const useDocumentStatusSummary = () =>
  useQuery({
    queryKey: queryKeys.tenantProfile.documents.statusSummary,
    queryFn: fetchStatusSummary,
    refetchInterval: (query) => {
      const byStatus = query.state.data?.byStatus;
      const busy =
        (byStatus?.[TenantProfileDocumentStatus.CLASSIFYING] ?? 0) +
        (byStatus?.[TenantProfileDocumentStatus.EXTRACTING] ?? 0);
      return busy > 0 ? POLL_INTERVAL_MS : false;
    },
  });

async function fetchSummary(): Promise<ProfileSummaryDto> {
  const { data } = await apiClient.get<ProfileSummaryDto>(
    'api/tenant-profile/summary',
  );
  return data;
}

export const useProfileSummary = () =>
  useQuery({
    queryKey: queryKeys.tenantProfile.summary,
    queryFn: fetchSummary,
  });

async function requestUploadUrl(input: {
  fileName: string;
  contentType: string;
}): Promise<UploadUrlResponse> {
  const { data } = await apiClient.post<UploadUrlResponse>(
    'api/tenant-profile/documents/upload-url',
    input,
  );
  return data;
}

export const useRequestUploadUrl = () =>
  useMutation({ mutationFn: requestUploadUrl });

async function confirmUpload(input: {
  documentId: string;
  s3Key: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}): Promise<ConfirmUploadResponse> {
  const { data } = await apiClient.post<ConfirmUploadResponse>(
    'api/tenant-profile/documents/confirm',
    input,
  );
  return data;
}

export const useConfirmUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: confirmUpload,
    onSuccess: () => {
      queryClient.invalidateQueries({
        // Prefix-invalidate so the full list, the paginated hub table and the
        // status-summary counts all refresh.
        queryKey: ['tenantProfile', 'documents'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.summary,
      });
    },
  });
};

async function deleteDocument(tenantProfileDocumentId: string): Promise<void> {
  await apiClient.delete(
    `api/tenant-profile/documents/${tenantProfileDocumentId}`,
  );
}

export const useDeleteProfileDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({
        // Prefix-invalidate so the full list, the paginated hub table and the
        // status-summary counts all refresh.
        queryKey: ['tenantProfile', 'documents'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.summary,
      });
    },
  });
};

async function bulkExtract(
  tenantProfileDocumentIds: string[],
): Promise<BulkExtractResponse> {
  const { data } = await apiClient.post<BulkExtractResponse>(
    'api/tenant-profile/documents/extract',
    { tenantProfileDocumentIds },
  );
  return data;
}

export const useBulkExtractDocuments = () => {
  const queryClient = useQueryClient();
  const onError = useBackendErrorToast('app.orgProfile.upload.extractFailed');
  return useMutation({
    mutationFn: bulkExtract,
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        // Prefix-invalidate so the full list, the paginated hub table and the
        // status-summary counts all refresh.
        queryKey: ['tenantProfile', 'documents'],
      });
    },
  });
};

async function retryClassify(
  tenantProfileDocumentIds: string[],
): Promise<RetryClassifyResponse> {
  const { data } = await apiClient.post<RetryClassifyResponse>(
    'api/tenant-profile/documents/classifications',
    { tenantProfileDocumentIds },
  );
  return data;
}

export const useRetryClassifyDocuments = () => {
  const queryClient = useQueryClient();
  const onError = useBackendErrorToast('app.orgProfile.upload.retryFailed');
  return useMutation({
    mutationFn: retryClassify,
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        // Prefix-invalidate so the full list, the paginated hub table and the
        // status-summary counts all refresh.
        queryKey: ['tenantProfile', 'documents'],
      });
    },
  });
};

async function fetchExtractionReview(
  tenantProfileDocumentId: string,
): Promise<ExtractionReviewDto> {
  const { data } = await apiClient.get<ExtractionReviewDto>(
    `api/tenant-profile/documents/${tenantProfileDocumentId}/extraction`,
  );
  return data;
}

export const useExtractionReview = (tenantProfileDocumentId: string | null) =>
  useQuery({
    queryKey: queryKeys.tenantProfile.extractionReview(
      tenantProfileDocumentId ?? '',
    ),
    queryFn: () => fetchExtractionReview(tenantProfileDocumentId as string),
    enabled: !!tenantProfileDocumentId,
  });

async function saveDocumentReview(input: {
  tenantProfileDocumentId: string;
  fieldValues: Record<string, unknown>;
  reviewedFieldKeys: string[];
}): Promise<VersionRef> {
  const { data } = await apiClient.post<VersionRef>(
    `api/tenant-profile/documents/${input.tenantProfileDocumentId}/review`,
    {
      fieldValues: input.fieldValues,
      reviewedFieldKeys: input.reviewedFieldKeys,
    },
  );
  return data;
}

// ── Reference-field upload ────────────────────────────────────────────────

async function requestReferenceUploadUrl(input: {
  fileName: string;
  contentType: string;
}): Promise<UploadUrlResponse> {
  const { data } = await apiClient.post<UploadUrlResponse>(
    'api/tenant-profile/references/upload-url',
    input,
  );
  return data;
}

export const useRequestReferenceUploadUrl = () =>
  useMutation({ mutationFn: requestReferenceUploadUrl });

async function confirmReferenceUpload(input: {
  fieldId: string;
  documentId: string;
  s3Key: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}): Promise<ProfileReferenceFileDto> {
  const { data } = await apiClient.post<ProfileReferenceFileDto>(
    'api/tenant-profile/references/confirm',
    input,
  );
  return data;
}

export const useConfirmReferenceUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: confirmReferenceUpload,
    onSuccess: () => {
      // Refresh the category so the pending file is in the query cache.
      // Without this, soft-navigation (navigate away + back) re-initialises the
      // form from stale cached data that doesn't include the new pending entry.
      // The reference field's owning category isn't known here, so refetch the
      // currently-mounted category via the category-key prefix.
      queryClient.invalidateQueries({
        queryKey: ['tenantProfile', 'template', 'category'],
      });
    },
  });
};

async function discardReferenceUpload(documentId: string): Promise<void> {
  await apiClient.delete(`api/tenant-profile/references/${documentId}`);
}

export const useDiscardReferenceUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: discardReferenceUpload,
    onSuccess: () => {
      // Keep the cache in sync so a discarded file doesn't reappear when the
      // category is next read from cache.
      queryClient.invalidateQueries({
        queryKey: ['tenantProfile', 'template', 'category'],
      });
    },
  });
};

// ── Document review ───────────────────────────────────────────────────────

/**
 * Review save is synchronous: it mints a TenantProfileVersion in one request
 * and flips the source document to `published`. Refresh the document list +
 * template values on success. A concurrent same-category save fails with a
 * 409 surfaced via the mutation error.
 */
export const useSaveDocumentReview = () => {
  const queryClient = useQueryClient();
  const onError = useBackendErrorToast('app.orgProfile.banner.saveFailed');
  return useMutation({
    mutationFn: saveDocumentReview,
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        // Prefix-invalidate so the full list, the paginated hub table and the
        // status-summary counts all refresh.
        queryKey: ['tenantProfile', 'documents'],
      });
      // A review save can write values across several categories, so refresh
      // the outline (counts) and every cached category.
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.templateOutline,
      });
      queryClient.invalidateQueries({
        queryKey: ['tenantProfile', 'template', 'category'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.summary,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.versions,
      });
    },
  });
};
